import { useCallback, useState } from 'react';
import {
  OPEN_ENDED_QUANTITY_TO,
  specificClientPriceRangesSchema,
} from '@/schemas/commercial/specific-client-price.schema';
import type {
  ISpecificClientPriceRange,
  SpecificClientPriceRangePayload,
  SpecificClientPriceRangePaymentType,
} from '@/types/commercial/specific-client-prices';

let tierIdCounter = 0;

export interface SpecificClientPriceTier {
  id: number;
  from: number | '';
  to: number | '';
  price: number | '';
}

type TierPatch = Partial<Pick<SpecificClientPriceTier, 'from' | 'to' | 'price'>>;

interface NormalizeTiersOptions {
  fillEmptyNonLastTo?: boolean;
}

function nextTierId(): number {
  tierIdCounter += 1;
  return tierIdCounter;
}

function normalizeTiers(
  tiers: SpecificClientPriceTier[],
  { fillEmptyNonLastTo = true }: NormalizeTiersOptions = {},
): SpecificClientPriceTier[] {
  const padded =
    tiers.length >= 2
      ? tiers
      : [
          ...tiers,
          {
            id: nextTierId(),
            from: '' as const,
            to: '' as const,
            price: '' as const,
          },
        ];

  return padded.map((tier, index) => {
    if (index === 0) return { ...tier, from: 1 };

    if (index === padded.length - 1) {
      return { ...tier, to: '' as const };
    }

    // Non-last tier that was previously open-ended: default to = from
    if (fillEmptyNonLastTo && tier.to === '' && tier.from !== '') {
      return { ...tier, to: tier.from };
    }

    return tier;
  });
}

export function updateSpecificClientPriceTier(
  tiers: SpecificClientPriceTier[],
  index: number,
  patch: TierPatch,
): SpecificClientPriceTier[] {
  const nextFrom = typeof patch.to === 'number' && Number.isFinite(patch.to) ? patch.to + 1 : null;

  return tiers.map((tier, currentIndex) => {
    if (currentIndex === index) return { ...tier, ...patch };

    if (currentIndex === index + 1 && nextFrom !== null) {
      return { ...tier, from: nextFrom };
    }

    return tier;
  });
}

export function makeSpecificClientPriceTiers(): SpecificClientPriceTier[] {
  return normalizeTiers([
    { id: nextTierId(), from: 1, to: 1, price: '' },
    { id: nextTierId(), from: '', to: '', price: '' },
  ]);
}

export function specificClientPriceRangeToTier(
  range: ISpecificClientPriceRange,
): SpecificClientPriceTier {
  return {
    id: nextTierId(),
    from: range.quantityFrom,
    to: range.quantityTo === OPEN_ENDED_QUANTITY_TO ? '' : range.quantityTo,
    price: range.priceInCents / 100,
  };
}

export function normalizeSpecificClientPriceTiers(
  tiers: SpecificClientPriceTier[],
): SpecificClientPriceTier[] {
  return normalizeTiers(tiers);
}

export function useSpecificClientPriceRanges(initialTiers = makeSpecificClientPriceTiers()) {
  const [tiers, setTiers] = useState<SpecificClientPriceTier[]>(() => normalizeTiers(initialTiers));

  const replaceTiers = useCallback((nextTiers: SpecificClientPriceTier[]) => {
    setTiers(normalizeTiers(nextTiers));
  }, []);

  const addTier = useCallback(() => {
    setTiers((prev) => {
      const newTier: SpecificClientPriceTier = {
        id: nextTierId(),
        from: '' as const,
        to: '',
        price: '' as const,
      };

      return normalizeTiers([...prev, newTier]);
    });
  }, []);

  const updateTier = useCallback((index: number, patch: TierPatch) => {
    setTiers((prev) =>
      normalizeTiers(updateSpecificClientPriceTier(prev, index, patch), {
        fillEmptyNonLastTo: false,
      }),
    );
  }, []);

  const removeTier = useCallback((index: number) => {
    setTiers((prev) => {
      if (prev.length <= 2) return normalizeTiers(prev);
      return normalizeTiers(prev.filter((_, currentIndex) => currentIndex !== index));
    });
  }, []);

  return {
    tiers,
    replaceTiers,
    addTier,
    updateTier,
    removeTier,
  };
}

export function validateSpecificClientPriceRanges(
  ranges: SpecificClientPriceRangePayload[],
): string | null {
  const result = specificClientPriceRangesSchema.safeParse(ranges);
  return result.success ? null : (result.error.issues[0]?.message ?? 'Faixas invalidas');
}

export function buildSpecificClientPriceRanges(
  paymentType: SpecificClientPriceRangePaymentType,
  tiersEnabled: boolean,
  tiers: SpecificClientPriceTier[],
  reaisToCents: (value?: number | '') => number,
): SpecificClientPriceRangePayload[] {
  if (!tiersEnabled) return [];

  const normalizedTiers = normalizeTiers(tiers, { fillEmptyNonLastTo: false });

  return normalizedTiers
    .map((tier, index) => ({
      isLast: index === normalizedTiers.length - 1,
      tier,
    }))
    .filter(({ tier }) => tier.price !== '')
    .map(({ tier, isLast }) => {
      return {
        paymentType,
        quantityFrom: tier.from === '' ? 0 : tier.from,
        ...(!isLast || tier.to !== '' ? { quantityTo: tier.to === '' ? 0 : tier.to } : {}),
        priceInCents: reaisToCents(tier.price),
      };
    });
}
